import { Button, Select } from '@nazka/nazka.mapframe.components';
import { useForm, Controller } from 'react-hook-form';

import { useTranslation } from '@utils/i18n/i18n.consumers';

import { HAZARD_CATEGORIES } from '../../admin.constants';

import './admin-edit-story-form.styl';

function AdminEditStoryForm({
  story,
  availablePlans,
  onSuccess,
  onCancel,
  isPending,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    watch,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      title: story.translations?.en?.title || '',
      description: story.translations?.en?.description || '',
      linkedPlan: story.linkedPlan || '',
      responsible: story.responsible || '',
      hazardCategories: story.hazardCategories.map(id => HAZARD_CATEGORIES.find(c => c.id === id)) || [],
    },
  });

  const { t } = useTranslation();

  const onSubmit = (data) => {
    const updatePayload = {
      translations: {
        en: {
          title: data.title,
          description: data.description,
        },
      },
      linkedPlan: data.linkedPlan || null,
      responsible: data.responsible,
      hazardCategories: data.hazardCategories.map(category => category.id),
    };

    onSuccess(updatePayload);
  };

  const linkedPlan = watch('linkedPlan');

  return (
    <div className="admin-edit-story-form">
      <div className="admin-edit-story-form__content">
        <div className="admin-edit-story-form__field">
          <label htmlFor="title" className="admin-edit-story-form__label">
            {`${t('Title')} *`}
          </label>
          <input
            id="title"
            type="text"
            {...register('title', {
              required: t('Title is required.'),
              // minLength: { value: 3, message: t('Title must be at least 3 characters.') },
            })}
            className={`admin-edit-story-form__input ${errors.title ? 'admin-edit-story-form__input--error' : ''
            }`}
            placeholder={t('Enter story title')}
          />
          {errors.title && (
            <p className="admin-edit-story-form__error">{errors.title.message}</p>
          )}
        </div>

        <div className="admin-edit-story-form__field">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <label htmlFor="linkedPlan" className="admin-edit-story-form__label">
              {t('Linked plan')}
            </label>
            {linkedPlan && (
              <a
                href={`/admin/plans/${linkedPlan}`}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-edit-story-form__link"
              >
                {t('View linked plan')}
              </a>
            )}
          </div>
          <Controller
            name="linkedPlan"
            control={control}
            render={({ field }) => (
              <Select
                className="admin-edit-story-form__select"
                rootElProps={{ id: 'linkedPlan' }}
                value={field.value}
                onInputChange={value => field.onChange(value ? value.id : null)}
                options={availablePlans?.map(plan => ({ id: plan.id, label: plan.translations.en.title })) ?? []}
                clear
                placeholder={t('Select linked plan...')}
                css={{ borderColor: errors.linkedPlan ? '#dc3545' : '#ddd', color: errors.linkedPlan ? '#dc3545' : '#333' }}
              />
            )}
          />
        </div>

        <div className="admin-edit-story-form__field">
          <label htmlFor="hazardCategories" className="admin-edit-story-form__label">
            {`${t('Linked hazard')} *`}
          </label>
          <Controller
            name="hazardCategories"
            control={control}
            rules={{
              required: t('Please select at least one linked hazard.'),
              validate: (value) => {
                if (!value || value.length === 0) {
                  return t('Please select at least one linked hazard.');
                }

                return true;
              },
            }}
            render={({ field }) => (
              <Select
                className="admin-edit-story-form__select"
                rootElProps={{ id: 'hazardCategories' }}
                value={field.value}
                onInputChange={(value) => {
                  field.onBlur();
                  field.onChange(value);
                }}
                css={{ borderColor: errors.hazardCategories ? '#dc3545' : '#ddd', color: errors.hazardCategories ? '#dc3545' : '#333' }}
                options={HAZARD_CATEGORIES}
                isMulti
                placeholder={t('Select hazards...')}
              />
            )}
          />
          {errors.hazardCategories && (
            <p className="admin-edit-story-form__error">{errors.hazardCategories.message}</p>
          )}
        </div>

        <div className="admin-edit-story-form__field">
          <label htmlFor="description" className="admin-edit-story-form__label">
            {`${t('What is the adaptation story and how does it address the hazard?')} *`}
          </label>
          <textarea
            id="description"
            rows={4}
            {...register('description', {
              required: t('Description is required.'),
              // minLength: { value: 10, message: t('Description must be at least 10 characters.') },
            })}
            className={`admin-edit-story-form__textarea ${errors.description ? 'admin-edit-story-form__textarea--error' : ''
            }`}
            placeholder={t('Describe the adaptation story...')}
          />
          {errors.description && (
            <p className="admin-edit-story-form__error">{errors.description.message}</p>
          )}
        </div>

        <div className="admin-edit-story-form__field">
          <label htmlFor="responsible" className="admin-edit-story-form__label">
            {`${t('Who is responsible for this adaptation story?')} *`}
          </label>
          <input
            id="responsible"
            type="text"
            {...register('responsible', {
              required: t('Responsible party is required.'),
              // minLength: { value: 2, message: t('Responsible party must be at least 2 characters.') },
            })}
            className={`admin-edit-story-form__input ${errors.responsible ? 'admin-edit-story-form__input--error' : ''
            }`}
            placeholder={t('Who is responsible for this story?')}
          />
          {errors.responsible && (
            <p className="admin-edit-story-form__error">{errors.responsible.message}</p>
          )}
        </div>

        <div className="admin-edit-story-form__actions">
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="admin-edit-story-form__button"
            css={{
              color: 'white',
              '&:hover': {
                color: 'white',
              },
            }}
            disabled={!isValid}
            isLoading={isPending}
          >
            {t('Save changes')}
          </Button>
          <Button
            type="button"
            onClick={onCancel}
            className="admin-edit-story-form__button"
            layout="outlined"
          >
            {t('Cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminEditStoryForm;